// Original, self-hosted brand illustrations.
//
// These are generated as inline SVG data URIs so they ship inside the JS bundle
// with zero network requests and zero third-party assets. Nothing here is
// scraped, hotlinked, or AI-generated: every shape is drawn from scratch using
// the Mums Connect brand palette (see client/tailwind.config.js). That keeps the
// app's imagery legally clean (no licensing/attribution obligations) while still
// giving each card/section a warm, on-brand illustration.

import BRAND_COLORS from './brandColors';

const PALETTE = {
  surface: BRAND_COLORS.surface,
  white: BRAND_COLORS.white,
  primary: BRAND_COLORS.primary,
  primaryContainer: BRAND_COLORS.primaryContainer,
  secondary: BRAND_COLORS.secondary,
  secondaryContainer: BRAND_COLORS.secondaryContainer,
  tertiary: BRAND_COLORS.tertiary,
  tertiaryContainer: BRAND_COLORS.tertiaryContainer,
  accent: BRAND_COLORS.accent,
  accentContainer: BRAND_COLORS.accentContainer,
};

// Colour recipes. `ink` draws the motif, `badge` tints the centre disc, and the
// two blob colours add soft background depth.
const TONES = {
  primary: {
    ink: PALETTE.primary,
    badge: PALETTE.primaryContainer,
    blobA: PALETTE.primaryContainer,
    blobB: PALETTE.secondaryContainer,
  },
  secondary: {
    ink: PALETTE.secondary,
    badge: PALETTE.secondaryContainer,
    blobA: PALETTE.secondaryContainer,
    blobB: PALETTE.primaryContainer,
  },
  tertiary: {
    ink: PALETTE.tertiary,
    badge: PALETTE.tertiaryContainer,
    blobA: PALETTE.tertiaryContainer,
    blobB: PALETTE.primaryContainer,
  },
  accent: {
    ink: PALETTE.accent,
    badge: PALETTE.accentContainer,
    blobA: PALETTE.accentContainer,
    blobB: PALETTE.tertiaryContainer,
  },
};

// Every motif is drawn inside a 1200x900 canvas, centred on (600, 450) and kept
// within roughly a 150px radius so it sits neatly inside the badge disc.
const MOTIFS = {
  sun: (c) => `
    <circle cx="600" cy="450" r="78" fill="${c}"/>
    <g stroke="${c}" stroke-width="22" stroke-linecap="round">
      <path d="M600 318 v-44"/><path d="M600 626 v44"/>
      <path d="M468 450 h-44"/><path d="M732 450 h44"/>
      <path d="M512 362 l-30 -30"/><path d="M688 362 l30 -30"/>
      <path d="M512 538 l-30 30"/><path d="M688 538 l30 30"/>
    </g>`,

  heart: (c) => `
    <path d="M600 556 C500 482 470 410 524 372 C566 342 600 386 600 408 C600 386 634 342 676 372 C730 410 700 482 600 556 Z" fill="${c}"/>`,

  people: (c) => `
    <g fill="${c}">
      <circle cx="512" cy="408" r="44"/>
      <path d="M454 540 a58 58 0 0 1 116 0 Z"/>
      <circle cx="688" cy="408" r="44"/>
      <path d="M630 540 a58 58 0 0 1 116 0 Z"/>
      <circle cx="600" cy="386" r="50"/>
      <path d="M536 528 a64 64 0 0 1 128 0 Z"/>
    </g>`,

  leaf: (c) => `
    <path d="M600 332 C712 372 712 528 600 568 C488 528 488 372 600 332 Z" fill="${c}"/>
    <path d="M600 350 V552" stroke="${PALETTE.surface}" stroke-width="16" stroke-linecap="round"/>
    <path d="M600 430 l44 -34 M600 480 l-44 -34" stroke="${PALETTE.surface}" stroke-width="12" stroke-linecap="round"/>`,

  book: (c) => `
    <g stroke="${c}" stroke-width="22" stroke-linejoin="round" stroke-linecap="round">
      <path d="M600 378 C556 350 502 346 462 358 V548 C502 536 556 540 600 568 C644 540 698 536 738 548 V358 C698 346 644 350 600 378 Z"/>
      <path d="M600 378 V568"/>
    </g>`,

  calendar: (c) => `
    <g stroke="${c}" stroke-width="22" stroke-linejoin="round" stroke-linecap="round">
      <rect x="470" y="384" width="260" height="206" rx="26"/>
      <path d="M470 442 h260"/>
      <path d="M534 360 v44"/><path d="M666 360 v44"/>
    </g>
    <g fill="${c}" stroke="none">
      <circle cx="540" cy="494" r="13"/><circle cx="600" cy="494" r="13"/><circle cx="660" cy="494" r="13"/>
      <circle cx="540" cy="544" r="13"/><circle cx="600" cy="544" r="13"/>
    </g>`,

  speech: (c) => `
    <path d="M468 376 h264 a34 34 0 0 1 34 34 v104 a34 34 0 0 1 -34 34 h-150 l-62 52 v-52 h-52 a34 34 0 0 1 -34 -34 v-104 a34 34 0 0 1 34 -34 Z" stroke="${c}" stroke-width="22" stroke-linejoin="round"/>
    <g fill="${c}" stroke="none"><circle cx="540" cy="462" r="14"/><circle cx="600" cy="462" r="14"/><circle cx="660" cy="462" r="14"/></g>`,

  ticket: (c) => `
    <rect x="466" y="392" width="268" height="150" rx="28" stroke="${c}" stroke-width="22"/>
    <path d="M600 400 v134" stroke="${c}" stroke-width="16" stroke-linecap="round" stroke-dasharray="2 28"/>`,

  music: (c) => `
    <path d="M566 548 V382 l128 -36 V506" stroke="${c}" stroke-width="22" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="538" cy="552" r="36" fill="${c}" stroke="none"/>
    <circle cx="666" cy="512" r="36" fill="${c}" stroke="none"/>`,

  waves: (c) => `
    <g stroke="${c}" stroke-width="22" stroke-linecap="round">
      <path d="M472 408 q38 -36 76 0 t76 0 t76 0 t28 -12"/>
      <path d="M472 470 q38 -36 76 0 t76 0 t76 0 t28 -12"/>
      <path d="M472 532 q38 -36 76 0 t76 0 t76 0 t28 -12"/>
    </g>`,

  footsteps: (c) => `
    <g fill="${c}" stroke="none">
      <ellipse cx="552" cy="424" rx="34" ry="50" transform="rotate(-14 552 424)"/>
      <circle cx="540" cy="372" r="13"/><circle cx="561" cy="368" r="11"/>
      <ellipse cx="648" cy="500" rx="34" ry="50" transform="rotate(-14 648 500)"/>
      <circle cx="636" cy="448" r="13"/><circle cx="657" cy="444" r="11"/>
    </g>`,

  lotus: (c) => `
    <g fill="${c}" stroke="none">
      <path d="M600 360 C642 422 642 480 600 520 C558 480 558 422 600 360 Z"/>
      <path d="M600 520 C556 484 496 476 462 488 C494 540 558 548 600 520 Z" opacity="0.82"/>
      <path d="M600 520 C644 484 704 476 738 488 C706 540 642 548 600 520 Z" opacity="0.82"/>
      <path d="M600 520 C566 470 520 440 486 430 C486 482 540 520 600 520 Z" opacity="0.6"/>
      <path d="M600 520 C634 470 680 440 714 430 C714 482 660 520 600 520 Z" opacity="0.6"/>
    </g>`,

  dumbbell: (c) => `
    <g stroke="${c}" stroke-width="24" stroke-linecap="round">
      <path d="M520 450 h160"/>
      <path d="M502 408 v84"/><path d="M468 426 v48"/>
      <path d="M698 408 v84"/><path d="M732 426 v48"/>
    </g>`,

  sparkles: (c) => `
    <g fill="${c}" stroke="none">
      <path d="M600 356 l18 50 50 18 -50 18 -18 50 -18 -50 -50 -18 50 -18 Z"/>
      <path d="M506 474 l11 30 30 11 -30 11 -11 30 -11 -30 -30 -11 30 -11 Z" opacity="0.82"/>
      <path d="M684 502 l9 24 24 9 -24 9 -9 24 -9 -24 -24 -9 24 -9 Z" opacity="0.82"/>
    </g>`,

  balloon: (c) => `
    <ellipse cx="600" cy="432" rx="74" ry="86" fill="${c}" stroke="none"/>
    <path d="M600 518 l-12 26 24 0 Z" fill="${c}" stroke="none"/>
    <path d="M600 544 q22 28 0 58 t0 40" stroke="${c}" stroke-width="10" stroke-linecap="round"/>`,

  flower: (c) => `
    <g fill="${c}" stroke="none">
      <circle cx="600" cy="366" r="44"/>
      <circle cx="600" cy="534" r="44"/>
      <circle cx="516" cy="450" r="44"/>
      <circle cx="684" cy="450" r="44"/>
      <circle cx="541" cy="391" r="44" opacity="0.9"/>
      <circle cx="659" cy="391" r="44" opacity="0.9"/>
      <circle cx="541" cy="509" r="44" opacity="0.9"/>
      <circle cx="659" cy="509" r="44" opacity="0.9"/>
    </g>
    <circle cx="600" cy="450" r="48" fill="${PALETTE.surface}" stroke="none"/>`,

  lightbulb: (c) => `
    <path d="M600 356 a104 104 0 0 1 64 186 q-12 10 -12 30 h-104 q0 -20 -12 -30 a104 104 0 0 1 64 -186 Z" stroke="${c}" stroke-width="22" stroke-linejoin="round"/>
    <g stroke="${c}" stroke-width="20" stroke-linecap="round"><path d="M556 566 h88"/><path d="M566 598 h68"/></g>`,

  mug: (c) => `
    <path d="M510 398 h178 v54 a89 72 0 0 1 -178 0 Z" stroke="${c}" stroke-width="22" stroke-linejoin="round"/>
    <path d="M688 414 a48 48 0 0 1 0 96" stroke="${c}" stroke-width="22"/>
    <g stroke="${c}" stroke-width="15" stroke-linecap="round">
      <path d="M556 360 q16 -20 0 -40"/><path d="M600 360 q16 -20 0 -40"/><path d="M644 360 q16 -20 0 -40"/>
    </g>`,

  bowl: (c) => `
    <path d="M464 454 h272 a136 136 0 0 1 -272 0 Z" stroke="${c}" stroke-width="22" stroke-linejoin="round"/>
    <path d="M452 454 h296" stroke="${c}" stroke-width="22" stroke-linecap="round"/>
    <g stroke="${c}" stroke-width="15" stroke-linecap="round">
      <path d="M512 372 q18 22 0 44"/><path d="M600 366 q18 22 0 44"/><path d="M688 372 q18 22 0 44"/>
    </g>`,

  apple: (c) => `
    <g fill="${c}" stroke="none">
      <ellipse cx="566" cy="476" rx="64" ry="78"/>
      <ellipse cx="634" cy="476" rx="64" ry="78"/>
    </g>
    <path d="M600 404 v-34" stroke="${PALETTE.tertiary}" stroke-width="14" stroke-linecap="round"/>
    <path d="M600 388 q34 -26 66 -14 q-6 36 -44 40 q-22 2 -22 -26 Z" fill="${PALETTE.tertiary}" stroke="none"/>`,

  shield: (c) => `
    <path d="M600 356 l128 46 v74 c0 96 -64 148 -128 178 c-64 -30 -128 -82 -128 -178 v-74 Z" stroke="${c}" stroke-width="22" stroke-linejoin="round"/>
    <path d="M552 470 l32 32 64 -70" stroke="${c}" stroke-width="22" stroke-linecap="round" stroke-linejoin="round"/>`,

  coin: (c) => `
    <circle cx="600" cy="450" r="120" stroke="${c}" stroke-width="22"/>
    <path d="M600 384 v132" stroke="${c}" stroke-width="20" stroke-linecap="round"/>
    <path d="M574 410 h40 a26 26 0 0 1 0 52 h-32 a26 26 0 0 0 0 52 h44" stroke="${c}" stroke-width="18" stroke-linecap="round" stroke-linejoin="round"/>`,

  bag: (c) => `
    <path d="M492 432 h216 l-18 170 a28 28 0 0 1 -28 26 h-124 a28 28 0 0 1 -28 -26 Z" stroke="${c}" stroke-width="22" stroke-linejoin="round"/>
    <path d="M548 432 v-16 a52 52 0 0 1 104 0 v16" stroke="${c}" stroke-width="22" stroke-linecap="round"/>`,
};

function blobs(a, b) {
  return `
    <path d="M-60 240 C120 96 360 150 432 322 C500 486 322 660 150 650 C-20 640 -160 432 -60 240 Z" fill="${a}" opacity="0.55"/>
    <path d="M1264 660 C1104 804 858 760 800 588 C742 420 932 268 1104 290 C1276 312 1364 516 1264 660 Z" fill="${b}" opacity="0.5"/>
    <circle cx="978" cy="172" r="48" fill="${a}" opacity="0.45"/>
    <circle cx="232" cy="752" r="34" fill="${b}" opacity="0.45"/>
    <circle cx="1066" cy="772" r="22" fill="${a}" opacity="0.4"/>`;
}

function svg(inner) {
  const doc =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 900" fill="none">' +
    `<rect width="1200" height="900" fill="${PALETTE.surface}"/>` +
    inner +
    '</svg>';
  return `data:image/svg+xml,${encodeURIComponent(doc)}`;
}

// Compose a finished illustration: soft background blobs, a tinted badge disc,
// and the chosen motif drawn on top. Returns a ready-to-use image URL string.
export function illustration(motif, tone = 'primary') {
  const t = TONES[tone] || TONES.primary;
  const draw = MOTIFS[motif] || MOTIFS.flower;
  const inner =
    blobs(t.blobA, t.blobB) +
    `<circle cx="600" cy="450" r="248" fill="${PALETTE.white}" opacity="0.7"/>` +
    `<circle cx="600" cy="450" r="248" fill="${t.badge}" opacity="0.5"/>` +
    `<circle cx="600" cy="450" r="248" stroke="${t.ink}" stroke-width="6" opacity="0.25"/>` +
    `<g fill="none">${draw(t.ink)}</g>`;
  return svg(inner);
}

// Lightweight standalone accent icon: just the motif shape, tightly cropped,
// with a fully transparent background — no blobs, no badge disc. Meant for
// small decorative "stickers" (e.g. a tiny floating heart/sparkle next to a
// hero graphic), not as a stretched/cropped background image. `tone` accepts
// a known palette tone ('primary' | 'secondary' | 'tertiary') or a literal
// CSS colour (e.g. '#ffffff') so accents can be recoloured for the surface
// they sit on.
export function motifAccent(motif, tone = 'primary') {
  const ink = TONES[tone]?.ink || tone || PALETTE.primary;
  const draw = MOTIFS[motif] || MOTIFS.flower;
  const size = 350;
  const half = size / 2;
  const doc =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${600 - half} ${450 - half} ${size} ${size}" fill="none">` +
    `<g fill="none">${draw(ink)}</g>` +
    '</svg>';
  return `data:image/svg+xml,${encodeURIComponent(doc)}`;
}

// Places a copy of an existing motif (drawn natively around 600,450) at an
// arbitrary position/scale via an SVG transform. Lets larger scene
// compositions (see `heroScene`) safely reuse proven motif artwork instead of
// hand-deriving new path coordinates.
function placeMotif(motif, color, targetX, targetY, scale) {
  const draw = MOTIFS[motif] || MOTIFS.flower;
  const tx = targetX - 600 * scale;
  const ty = targetY - 450 * scale;
  return `<g transform="translate(${tx} ${ty}) scale(${scale})">${draw(color)}</g>`;
}

// A reusable wide banner scene: soft tone-tinted blobs plus a handful of
// scattered motif accents, biased toward the right side of the frame so
// left-aligned headline text stays readable. Used for section-tinted hero
// banners (e.g. the Resources hub) where a full custom `heroScene()`-style
// composition isn't warranted, but the flat polka-dot placeholder isn't
// either. `motifKeys` accepts up to 3 motif names; positions/scale are
// fixed so callers don't need to hand-tune coordinates.
const BANNER_SLOTS = [
  { x: 1180, y: 210, scale: 0.32, opacity: 0.9 },
  { x: 1380, y: 470, scale: 0.24, opacity: 0.75 },
  { x: 980, y: 520, scale: 0.18, opacity: 0.6 },
];

export function bannerScene(tone = 'primary', motifKeys = ['leaf', 'heart', 'sparkles']) {
  const t = TONES[tone] || TONES.primary;
  const W = 1600;
  const H = 620;

  const sky =
    '<defs><linearGradient id="bannerSky" x1="0" y1="0" x2="1" y2="1">' +
    `<stop offset="0%" stop-color="${PALETTE.surface}"/>` +
    `<stop offset="100%" stop-color="${t.badge}"/>` +
    '</linearGradient></defs>' +
    `<rect width="${W}" height="${H}" fill="url(#bannerSky)"/>`;

  const bg = blobs(t.blobA, t.blobB);

  const motifsSvg = motifKeys
    .slice(0, 3)
    .map((key, i) => {
      const slot = BANNER_SLOTS[i];
      const draw = MOTIFS[key] || MOTIFS.flower;
      const tx = slot.x - 600 * slot.scale;
      const ty = slot.y - 450 * slot.scale;
      return `<g opacity="${slot.opacity}" transform="translate(${tx} ${ty}) scale(${slot.scale})">${draw(t.ink)}</g>`;
    })
    .join('');

  const doc =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" fill="none">` +
    sky + bg + motifsSvg +
    '</svg>';

  return `data:image/svg+xml,${encodeURIComponent(doc)}`;
}


// mums and kids meeting up together, designed to fill an entire full-bleed
// hero banner (object-cover) rather than being cropped out of a small icon
// badge. Composed once, at the correct wide aspect ratio, so nothing gets
// stretched or randomly cropped. Content is biased toward the right two
// thirds of the frame, leaving the left calmer for headline text overlay.
export function heroScene() {
  const W = 1600;
  const H = 720;
  const canopyInk = PALETTE.primary;
  const trunkInk = PALETTE.tertiary;
  const fireCore = '#f2ae53';
  const fireGlow = '#f7d8a0';
  const fireOuter = '#e07f47';

  const sky =
    '<defs><linearGradient id="heroSky" x1="0" y1="0" x2="0" y2="1">' +
    `<stop offset="0%" stop-color="${PALETTE.surface}"/>` +
    `<stop offset="100%" stop-color="${PALETTE.primaryContainer}"/>` +
    '</linearGradient></defs>' +
    `<rect width="${W}" height="${H}" fill="url(#heroSky)"/>`;

  const sun =
    `<circle cx="1310" cy="150" r="104" fill="${PALETTE.tertiaryContainer}" opacity="0.35"/>` +
    `<circle cx="1310" cy="150" r="64" fill="${PALETTE.tertiaryContainer}"/>`;

  const birds =
    `<path d="M400 120 q14 -14 28 0 q14 14 28 0" stroke="${canopyInk}" stroke-width="5" fill="none" stroke-linecap="round" opacity="0.5"/>` +
    `<path d="M470 88 q12 -12 24 0 q12 12 24 0" stroke="${canopyInk}" stroke-width="4" fill="none" stroke-linecap="round" opacity="0.5"/>`;

  const hillBack = `<path d="M0 640 Q800 520 1600 610 L1600 720 L0 720 Z" fill="${PALETTE.secondaryContainer}" opacity="0.55"/>`;
  const hillFront = `<path d="M0 700 Q800 590 1600 680 L1600 720 L0 720 Z" fill="${PALETTE.primaryContainer}" opacity="0.92"/>`;

  const treeLeft =
    `<rect x="96" y="520" width="16" height="130" rx="8" fill="${trunkInk}"/>` +
    `<g fill="${canopyInk}" opacity="0.88">` +
    '<circle cx="104" cy="460" r="66"/><circle cx="66" cy="492" r="46"/><circle cx="146" cy="492" r="46"/>' +
    '</g>';

  const treeRight =
    `<rect x="1494" y="548" width="14" height="100" rx="7" fill="${trunkInk}"/>` +
    `<g fill="${canopyInk}" opacity="0.88">` +
    '<circle cx="1501" cy="500" r="52"/><circle cx="1470" cy="524" r="36"/><circle cx="1534" cy="524" r="36"/>' +
    '</g>';

  // Warm campfire in a cozy little village clearing.
  const firePit =
    `<ellipse cx="1040" cy="598" rx="88" ry="26" fill="${PALETTE.tertiaryContainer}" opacity="0.65"/>` +
    `<circle cx="1040" cy="560" r="72" fill="${fireGlow}" opacity="0.32"/>` +
    `<circle cx="1040" cy="560" r="46" fill="${fireGlow}" opacity="0.46"/>` +
    `<path d="M1000 598 L1040 526 L1080 598" fill="${fireOuter}" opacity="0.95"/>` +
    `<path d="M1024 598 L1040 544 L1056 598" fill="${fireCore}"/>` +
    `<path d="M994 588 L1086 610" stroke="${trunkInk}" stroke-width="10" stroke-linecap="round" opacity="0.82"/>` +
    `<path d="M1086 588 L994 612" stroke="${trunkInk}" stroke-width="10" stroke-linecap="round" opacity="0.82"/>`;

  const hutA =
    `<rect x="846" y="506" width="118" height="90" rx="8" fill="${PALETTE.secondaryContainer}" opacity="0.94"/>` +
    `<path d="M834 506 L905 450 L976 506 Z" fill="${trunkInk}" opacity="0.92"/>` +
    `<rect x="894" y="548" width="24" height="48" rx="4" fill="${trunkInk}" opacity="0.86"/>` +
    `<circle cx="915" cy="560" r="17" fill="${fireGlow}" opacity="0.55"/>`;

  const hutB =
    `<rect x="1116" y="514" width="130" height="82" rx="8" fill="${PALETTE.primaryContainer}" opacity="0.95"/>` +
    `<path d="M1102 514 L1181 454 L1260 514 Z" fill="${trunkInk}" opacity="0.9"/>` +
    `<rect x="1172" y="550" width="26" height="46" rx="4" fill="${trunkInk}" opacity="0.84"/>` +
    `<rect x="1138" y="542" width="22" height="18" rx="4" fill="${fireGlow}" opacity="0.52"/>` +
    `<rect x="1208" y="542" width="22" height="18" rx="4" fill="${fireGlow}" opacity="0.52"/>`;

  const littleTrees =
    `<rect x="996" y="494" width="8" height="36" rx="4" fill="${trunkInk}"/>` +
    `<path d="M1000 460 L977 500 H1023 Z" fill="${canopyInk}" opacity="0.9"/>` +
    `<rect x="1088" y="486" width="8" height="34" rx="4" fill="${trunkInk}"/>` +
    `<path d="M1092 454 L1070 492 H1114 Z" fill="${canopyInk}" opacity="0.88"/>` +
    `<rect x="1298" y="502" width="8" height="38" rx="4" fill="${trunkInk}"/>` +
    `<path d="M1302 466 L1278 508 H1326 Z" fill="${canopyInk}" opacity="0.86"/>`;

  const accents =
    placeMotif('sparkles', fireGlow, 760, 140, 0.11) +
    placeMotif('sparkles', PALETTE.primary, 1220, 240, 0.09) +
    placeMotif('leaf', PALETTE.primary, 1548, 648, 0.15);

  const doc =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" fill="none">` +
    sky + sun + birds + hillBack + hillFront + treeLeft + treeRight +
    hutA + hutB + littleTrees + firePit + accents +
    '</svg>';

  return `data:image/svg+xml,${encodeURIComponent(doc)}`;
}

// Named illustrations used across the app (hero, welcome, empty states,
// activity categories, generic fallbacks).
export const ILLUSTRATIONS = {
  hero: illustration('sun', 'primary'),
  welcomeParent: illustration('heart', 'secondary'),
  welcomeOutdoor: illustration('sun', 'primary'),
  welcomeFriends: illustration('people', 'secondary'),
  loginBg: illustration('flower', 'primary'),
  emptyPosts: illustration('speech', 'primary'),
  emptyEvents: illustration('calendar', 'secondary'),
  emptyBookings: illustration('ticket', 'primary'),
  emptyResources: illustration('book', 'secondary'),
  meetup: illustration('people', 'primary'),
  yoga: illustration('lotus', 'secondary'),
  workshop: illustration('lightbulb', 'tertiary'),
  sensory: illustration('sparkles', 'secondary'),
  music: illustration('music', 'primary'),
  playgroup: illustration('balloon', 'secondary'),
  walk: illustration('footsteps', 'primary'),
  storytime: illustration('book', 'secondary'),
  swimming: illustration('waves', 'primary'),
  coffee: illustration('mug', 'secondary'),
  nature: illustration('leaf', 'primary'),
  baby: illustration('heart', 'secondary'),
  fitness: illustration('dumbbell', 'primary'),
  business: illustration('bag', 'primary'),
  brand: illustration('flower', 'primary'),
};

export default ILLUSTRATIONS;
